import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { getBalanceSheet, getProfitLoss, type StmtItem, type StockAt } from '@/api/accountsReports';
import type { ExportCell } from '../exportReport';
import { GroupSummaryReport } from './GroupSummaryReport';
import { Check, LedgerVouchersView, Loading, PeriodBar, ReportFrame } from './reportParts';
import { neg, num, tdate, td, useExcel, useKeyNav, usePeriod } from './reportUtils';

/*
 * Tally's horizontal statements: two halves side by side (stacked on a phone). Each item is a group
 * (amount in the outer column) with, when Detailed, its ledgers / sub-groups (inner column).
 */

type Row = { label: string; inner?: number; outer?: number; open?: () => void; kind: 'item' | 'child' | 'sub' | 'total' | 'blank' };
type Drill = { group?: { name: string; asOn: boolean }; ledger?: number };

const item = (label: string, amount: number, children: StmtItem[], det: boolean, open: (x: StmtItem) => (() => void) | undefined, self?: StmtItem): Row[] => [
  { label, outer: amount, open: self ? open(self) : undefined, kind: 'item' },
  ...(det ? children.map((c) => ({ label: c.label, inner: c.amount, open: open(c), kind: 'child' as const })) : []),
];

/** One half: its rows, padded so both halves line up on wide screens. */
function Half({ title, rows, start, rowProps }: { title: string; rows: Row[]; start: number; rowProps: (i: number, cls?: string) => object }) {
  let k = 0;
  const out: ReactNode[] = [];
  rows.forEach((r, i) => {
    out.push(
      r.kind === 'blank' ? <tr key={`b${i}`} className="hidden md:table-row"><td className={td}>&nbsp;</td><td /><td /></tr>
        : (
          <tr key={`${r.label}-${i}`} {...rowProps(start + k++, clsx(r.kind === 'sub' && 'border-t border-[var(--border-color)]', r.kind === 'total' && 'border-t-2 border-[var(--text-primary)] font-extrabold'))}>
            <td className={clsx(td, r.kind === 'item' && 'font-bold', r.kind === 'child' && 'pl-6 italic')}>{r.label}</td>
            <td className={clsx(num, 'italic')}>{r.inner != null ? neg(r.inner) : ''}</td>
            <td className={clsx(num, r.kind !== 'child' && 'font-bold')}>{r.outer != null ? neg(r.outer) : ''}</td>
          </tr>
        ),
    );
  });
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-[var(--border-color)]">
          <th className="px-2 py-1.5 text-left text-[11px] font-extrabold">{title}</th><th className="w-28" /><th className="w-32" />
        </tr>
      </thead>
      <tbody>{out}</tbody>
    </table>
  );
}

/** Lines up two lists of sections: the shorter one gets blank rows above its total, so both totals sit on one line. */
function sideBySide(sections: [Row[], Row[]][]) {
  const L: Row[] = [], R: Row[] = [];
  const blanks = (k: number) => Array.from({ length: k }, () => ({ label: '', kind: 'blank' as const }));
  const fill = (xs: Row[], n: number) => {
    const last = xs[xs.length - 1];
    const hasTotal = last && (last.kind === 'sub' || last.kind === 'total');
    return hasTotal ? [...xs.slice(0, -1), ...blanks(n - xs.length), last] : [...xs, ...blanks(n - xs.length)];
  };
  for (const [l, r] of sections) {
    const n = Math.max(l.length, r.length);
    L.push(...fill(l, n));
    R.push(...fill(r, n));
  }
  return [L, R] as const;
}

/** Where the stock figure came from; when nothing was entered, which date to enter it for. */
function StockNote({ s, label, on }: { s: StockAt; label: string; on: string }) {
  if (s.entered) return <>{label}: entered as on {tdate(s.asOn)}.</>;
  return (
    <span className="text-warning">
      {label}: none entered on or before {tdate(on)} — the Stock-in-Hand ledgers ({s.amount ? `₹${neg(s.amount)}` : 'nil'}) are used. Enter it in <Link to="/accounts/closing-stock" className="underline">Closing Stock</Link>.
    </span>
  );
}
const dayBefore = (d: string) => { const x = new Date(`${d}T00:00:00Z`); x.setUTCDate(x.getUTCDate() - 1); return x.toISOString().slice(0, 10); };

function useStatement(rowsL: Row[], rowsR: Row[], back?: () => void) {
  const live = (rs: Row[]) => rs.filter((r) => r.kind !== 'blank');
  const acts = useMemo(() => [...live(rowsL), ...live(rowsR)].map((r) => r.open ?? null), [rowsL, rowsR]);
  return { ...useKeyNav(acts, back), leftCount: live(rowsL).length };
}

const toExcel = (L: Row[], R: Row[]): ExportCell[][] => {
  const l = L, r = R, n = Math.max(l.length, r.length), out: ExportCell[][] = [];
  for (let i = 0; i < n; i++) {
    const a = l[i], b = r[i];
    out.push([a?.label ?? '', a?.inner ?? null, a?.outer ?? null, b?.label ?? '', b?.inner ?? null, b?.outer ?? null]);
  }
  return out;
};

export function ProfitLossPage() {
  const [p] = usePeriod();
  const [det, setDet] = useState(false);
  const [drill, setDrill] = useState<Drill>({});
  const q = useQuery({ queryKey: ['acc-books', 'reports', 'profit-loss', p.from, p.to], queryFn: () => getProfitLoss(p) });
  const d = q.data;
  const open = (x: StmtItem) => (x.groupId ? () => setDrill({ group: { name: x.label, asOn: false } }) : x.ledgerId ? () => setDrill({ ledger: x.ledgerId }) : undefined);
  const [L, R] = useMemo(() => {
    if (!d) return [[], []] as const;
    const gp = d.grossProfit, np = d.netProfit;
    const items = (xs: StmtItem[]) => xs.flatMap((x) => item(x.label, x.amount, x.children, det, open, x));
    const l1: Row[] = [{ label: 'Opening Stock', outer: d.openingStock.amount, kind: 'item' }, ...items(d.trading.dr), ...(gp > 0 ? [{ label: 'Gross Profit c/o', outer: gp, kind: 'item' as const }] : []), { label: '', outer: d.trading.total, kind: 'sub' }];
    const r1: Row[] = [...items(d.trading.cr), { label: 'Closing Stock', outer: d.closingStock.amount, kind: 'item' }, ...(gp < 0 ? [{ label: 'Gross Loss c/o', outer: -gp, kind: 'item' as const }] : []), { label: '', outer: d.trading.total, kind: 'sub' }];
    const l2: Row[] = [...(gp < 0 ? [{ label: 'Gross Loss b/f', outer: -gp, kind: 'item' as const }] : []), ...items(d.indirect.dr), ...(np > 0 ? [{ label: 'Nett Profit', outer: np, kind: 'item' as const }] : []), { label: 'Total', outer: d.indirect.total, kind: 'total' }];
    const r2: Row[] = [...(gp > 0 ? [{ label: 'Gross Profit b/f', outer: gp, kind: 'item' as const }] : []), ...items(d.indirect.cr), ...(np < 0 ? [{ label: 'Nett Loss', outer: -np, kind: 'item' as const }] : []), { label: 'Total', outer: d.indirect.total, kind: 'total' }];
    return sideBySide([[l1, r1], [l2, r2]]);
  }, [d, det]); // eslint-disable-line react-hooks/exhaustive-deps
  const nav = useStatement(L as Row[], R as Row[]);
  const excel = useExcel();
  if (drill.group) return <GroupSummaryReport title={drill.group.name} roots={[drill.group.name]} defaults={{ tx: true }} onBack={() => setDrill({})} />;
  if (drill.ledger) return <LedgerVouchersView ledgerId={drill.ledger} from={p.from} to={p.to} onBack={() => setDrill({})} />;
  return (
    <>
      <PeriodBar onExcel={d ? () => excel('Profit_and_Loss', 'Profit & Loss', ['Particulars', '', 'Amount', 'Particulars', '', 'Amount'], toExcel(L as Row[], R as Row[])) : undefined}>
        <Check label="Detailed (show ledgers)" checked={det} onChange={setDet} />
        {d && <span className="col-span-3 self-end pb-2 text-xs text-[var(--text-secondary)] sm:col-auto">{d.netProfit >= 0 ? 'Nett Profit' : 'Nett Loss'} <b>₹{neg(Math.abs(d.netProfit)) || '0.00'}</b></span>}
      </PeriodBar>
      <Loading q={q}>
        {d && (
          <ReportFrame title="Profit & Loss A/c" right={`${tdate(d.from)} to ${tdate(d.to)}`}
            note={<><StockNote s={d.openingStock} label="Opening stock" on={dayBefore(d.from)} /> <StockNote s={d.closingStock} label="Closing stock" on={d.to} /> Click a group for its summary, or a ledger (Detailed) for its vouchers — or use ↑ ↓ and Enter.</>}>
            <div className="grid min-w-0 md:grid-cols-2 md:divide-x md:divide-[var(--border-color)]">
              <Half title="Particulars" rows={L as Row[]} start={0} rowProps={nav.row} />
              <div className="border-t border-[var(--border-color)] md:border-t-0"><Half title="Particulars" rows={R as Row[]} start={nav.leftCount} rowProps={nav.row} /></div>
            </div>
          </ReportFrame>
        )}
      </Loading>
    </>
  );
}

export function BalanceSheetPage() {
  const [p] = usePeriod();
  const [det, setDet] = useState(false);
  const [drill, setDrill] = useState<Drill>({});
  const q = useQuery({ queryKey: ['acc-books', 'reports', 'balance-sheet', p.to], queryFn: () => getBalanceSheet(p.to) });
  const d = q.data;
  const open = (x: StmtItem) => (x.groupId ? () => setDrill({ group: { name: x.label, asOn: true } }) : x.ledgerId ? () => setDrill({ ledger: x.ledgerId }) : undefined);
  const [L, R] = useMemo(() => {
    if (!d) return [[], []] as const;
    const items = (xs: StmtItem[]) => xs.flatMap((x) => item(x.label, x.amount, x.children, det, open, x));
    const diff = Math.abs(d.difference) > 0.01;
    const l: Row[] = [...items(d.liabilities), ...item(d.profitLoss.label, d.profitLoss.amount, d.profitLoss.children, det, () => undefined),
      ...(diff ? [{ label: 'Difference in opening balances', outer: d.difference, kind: 'item' as const }] : [])];
    const r: Row[] = items(d.assets);
    const total = d.totalAssets;
    return sideBySide([[l, r], [[{ label: 'Total', outer: diff ? d.totalLiabilities + d.difference : d.totalLiabilities, kind: 'total' }], [{ label: 'Total', outer: total, kind: 'total' }]]]);
  }, [d, det]); // eslint-disable-line react-hooks/exhaustive-deps
  const nav = useStatement(L as Row[], R as Row[]);
  const excel = useExcel();
  if (drill.group) return <GroupSummaryReport title={drill.group.name} roots={[drill.group.name]} defaults={{ det: true }} asOn onBack={() => setDrill({})} />;
  if (drill.ledger) return <LedgerVouchersView ledgerId={drill.ledger} from={p.from} to={p.to} onBack={() => setDrill({})} />;
  return (
    <>
      <PeriodBar asOn onExcel={d ? () => excel('Balance_Sheet', 'Balance Sheet', ['Liabilities', '', 'Amount', 'Assets', '', 'Amount'], toExcel(L as Row[], R as Row[])) : undefined}>
        <Check label="Detailed (show sub-groups)" checked={det} onChange={setDet} />
        {d && <span className={clsx('col-span-3 self-end pb-2 text-xs sm:col-auto', Math.abs(d.difference) > 0.01 ? 'text-warning' : 'text-[var(--text-secondary)]')}>{Math.abs(d.difference) > 0.01 ? `Difference ₹${neg(d.difference)}` : 'Balanced ✓'}</span>}
      </PeriodBar>
      <Loading q={q}>
        {d && (
          <ReportFrame title="Balance Sheet" right={`as at ${tdate(d.asOn)}`}
            note={<><StockNote s={d.closingStock} label="Stock" on={d.asOn} /> The As on date sets the Balance Sheet date. Click a group for its summary — or use ↑ ↓ and Enter.</>}>
            <div className="grid min-w-0 md:grid-cols-2 md:divide-x md:divide-[var(--border-color)]">
              <Half title="Liabilities" rows={L as Row[]} start={0} rowProps={nav.row} />
              <div className="border-t border-[var(--border-color)] md:border-t-0"><Half title="Assets" rows={R as Row[]} start={nav.leftCount} rowProps={nav.row} /></div>
            </div>
          </ReportFrame>
        )}
      </Loading>
    </>
  );
}
